/**
 * SummaryDrawerContext — volet de synthèse épinglable (slot UNIQUE, éphémère).
 *
 * Mono-slot : une seule fiche épinglée à la fois (`pin` remplace la précédente).
 * État EN MÉMOIRE, jamais persisté serveur (RGPD). Desktop uniquement (≥1024px,
 * même seuil que le Help Drawer ancré) : sous 1024px le volet occuperait tout
 * l'écran et perdrait sa raison d'être (voir deux choses en parallèle) → il n'est
 * ni proposé ni ouvrable, et un volet ouvert est PURGÉ au passage desktop→mobile.
 */

import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react'

import type { PinnedSummary } from '@/components/summary/types'
import {
    SUMMARY_DESKTOP_QUERY,
    SummaryDrawerContext,
    isSummaryDesktop,
    type SummaryDrawerContextValue,
} from './SummaryDrawerContext.helpers'

export function SummaryDrawerProvider({ children }: { children: React.ReactNode }) {
    const [pinned, setPinned] = useState<PinnedSummary | null>(null)
    const [isDesktop, setIsDesktop] = useState<boolean>(isSummaryDesktop)

    useEffect(() => {
        const mql = window.matchMedia(SUMMARY_DESKTOP_QUERY)
        const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
        setIsDesktop(mql.matches)
        mql.addEventListener('change', onChange)
        return () => mql.removeEventListener('change', onChange)
    }, [])

    const pin = useCallback((target: PinnedSummary) => {
        // Garde desktop : on n'épingle jamais sous 1024px (l'entrée est déjà
        // retirée des kebabs, mais on double la garde ici par sécurité).
        if (!isSummaryDesktop()) return
        setPinned(target)
    }, [])

    const unpin = useCallback(() => setPinned(null), [])

    // Purge au passage desktop → mobile (le volet ne survit pas au rétrécissement).
    useEffect(() => {
        if (!isDesktop) setPinned(null)
    }, [isDesktop])

    const value = useMemo<SummaryDrawerContextValue>(
        () => ({ pinned, pin, unpin, isDesktop, visible: !!pinned && isDesktop }),
        [pinned, pin, unpin, isDesktop],
    )

    return <SummaryDrawerContext.Provider value={value}>{children}</SummaryDrawerContext.Provider>
}
